import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, glow } from "./shared";

// ── Shared "before → after" frame for the trust/quality upgrade scenes ──────
// Every upgrade scene renders one of these: a labelled Before column (muted)
// and an After column (green, with a ✓ badge), separated by a → arrow. The
// per-column content is passed in as `before` / `after` nodes so each scene
// stays self-contained and readable.

interface TrustCardProps {
  accent: string;
  index: string; // e.g. "1 / 4"
  title: string; // capability name
  subtitle: string; // one-line, plain-language explanation
  before: React.ReactNode;
  after: React.ReactNode;
}

export const TrustCard: React.FC<TrustCardProps> = ({
  accent,
  index,
  title,
  subtitle,
  before,
  after,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fade = interpolate(frame, [durationInFrames - 15, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Header in
  const headSp = spring({ frame, fps, config: SPRING.gentle });
  const headOp = interpolate(headSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const headY = interpolate(headSp, [0, 1], [-30, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Before column slides in from the left
  const bSp = spring({ frame: frame - 18, fps, config: SPRING.gentle });
  const bOp = interpolate(bSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const bX = interpolate(bSp, [0, 1], [-80, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // After column slides in from the right
  const aSp = spring({ frame: frame - 55, fps, config: SPRING.gentle });
  const aOp = interpolate(aSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const aX = interpolate(aSp, [0, 1], [80, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Center arrow
  const arSp = spring({ frame: frame - 45, fps, config: SPRING.punch });
  const arOp = interpolate(arSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const arSc = interpolate(arSp, [0, 1], [0.4, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // ✓ badge on the After column
  const ckSp = spring({ frame: frame - 78, fps, config: SPRING.punch });
  const ckSc = interpolate(ckSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fade, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={accent} />

      {/* Header: index · capability + plain explanation */}
      <div style={{
        position: "absolute", top: 66, left: 0, right: 0,
        textAlign: "center", opacity: headOp, transform: `translateY(${headY}px)`,
      }}>
        <div style={{
          display: "inline-block",
          fontSize: 20, fontFamily: FONTS.mono, color: accent,
          letterSpacing: 4, textTransform: "uppercase", fontWeight: 800,
          padding: "8px 22px", borderRadius: 30,
          background: `${accent}15`, border: `1.5px solid ${accent}`,
          textShadow: glow(accent),
        }}>
          {index} · {title}
        </div>
        <div style={{
          fontSize: 30, fontFamily: FONTS.body, color: COLORS.textSecondary,
          marginTop: 22, maxWidth: 1400, marginLeft: "auto", marginRight: "auto",
          lineHeight: 1.4,
        }}>
          {subtitle}
        </div>
      </div>

      {/* Two columns */}
      <div style={{
        position: "absolute", top: 320, left: 0, right: 0,
        display: "flex", justifyContent: "center", alignItems: "stretch", gap: 90,
      }}>
        {/* BEFORE */}
        <div style={{ width: 720, opacity: bOp, transform: `translateX(${bX}px)` }}>
          <ColumnLabel text="Before" color={COLORS.textMuted} icon="" />
          <div style={{
            marginTop: 16, minHeight: 500,
            background: `linear-gradient(135deg, ${COLORS.bgCardLight}aa, ${COLORS.bgCard}ee)`,
            border: `2px solid ${COLORS.textMuted}55`,
            borderRadius: 22, padding: "34px 38px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
          }}>
            {before}
          </div>
        </div>

        {/* AFTER */}
        <div style={{ width: 720, opacity: aOp, transform: `translateX(${aX}px)`, position: "relative" }}>
          <ColumnLabel text="After" color={COLORS.neonGreen} icon="✓" />
          <div style={{
            marginTop: 16, minHeight: 500,
            background: `linear-gradient(135deg, ${COLORS.neonGreen}12, ${COLORS.bgCard}ee)`,
            border: `2.5px solid ${COLORS.neonGreen}`,
            borderRadius: 22, padding: "34px 38px",
            boxShadow: `0 0 50px ${COLORS.neonGreen}44, 0 20px 40px rgba(0,0,0,0.4)`,
          }}>
            {after}
          </div>
          {/* ✓ badge */}
          <div style={{
            position: "absolute", top: 46, right: -28,
            width: 68, height: 68, borderRadius: "50%",
            background: COLORS.bg, border: `3px solid ${COLORS.neonGreen}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 38, color: COLORS.neonGreen,
            textShadow: glow(COLORS.neonGreen, true),
            boxShadow: `0 0 30px ${COLORS.neonGreen}88`,
            transform: `scale(${ckSc})`,
          }}>
            ✓
          </div>
        </div>
      </div>

      {/* Center → arrow, sitting in the column gap */}
      <div style={{
        position: "absolute", top: 590, left: "50%",
        transform: `translate(-50%, -50%) scale(${arSc})`,
        opacity: arOp, fontSize: 58, fontWeight: 900,
        color: accent, textShadow: glow(accent, true),
      }}>
        →
      </div>
    </AbsoluteFill>
  );
};

const ColumnLabel: React.FC<{ text: string; color: string; icon: string }> = ({ text, color, icon }) => (
  <div style={{
    fontSize: 22, fontFamily: FONTS.mono, fontWeight: 800,
    color, letterSpacing: 3, textTransform: "uppercase",
    display: "flex", alignItems: "center", gap: 10,
  }}>
    {icon ? <span style={{ textShadow: glow(color) }}>{icon}</span> : null}
    {text}
  </div>
);

// ── Presentational atoms shared by the upgrade scenes ───────────────────────

export const paraStyle: React.CSSProperties = {
  fontSize: 29, fontFamily: FONTS.body, color: COLORS.textPrimary,
  lineHeight: 1.5, margin: "20px 0 24px",
};

/** Inline highlight for a key fact inside an answer. */
export const Hi: React.FC<{ c: string; children: React.ReactNode }> = ({ c, children }) => (
  <span style={{ color: c, fontWeight: 800, textShadow: `0 0 18px ${c}aa` }}>{children}</span>
);

/** Small labelled note pinned at the bottom of a column. */
export const Tag: React.FC<{ color: string; text: string }> = ({ color, text }) => (
  <div style={{
    display: "inline-block", fontSize: 20, fontFamily: FONTS.mono, fontWeight: 700,
    color, padding: "8px 18px", borderRadius: 10,
    background: `${color}12`, border: `1.5px solid ${color}66`,
  }}>
    {text}
  </div>
);

/** The "❯ question" line shown at the top of a card. */
export const MiniQuery: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    fontSize: 21, fontFamily: FONTS.mono, color: COLORS.textSecondary,
    display: "flex", gap: 12, alignItems: "center",
    paddingBottom: 16, borderBottom: `1px solid ${COLORS.textMuted}44`,
  }}>
    <span style={{ color: COLORS.neonCyan, fontWeight: 900 }}>❯</span>
    <span>{text}</span>
  </div>
);

/** A single source row for the "honest citations" scene. */
export const SourceRow: React.FC<{
  icon: string;
  name: string;
  c: string;
  mode: "plain" | "flagged" | "cited";
}> = ({ icon, name, c, mode }) => {
  const flagged = mode === "flagged";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 16,
      padding: "15px 20px", borderRadius: 12, marginBottom: 13,
      background: flagged ? `${COLORS.neonRed}12` : `${c}12`,
      border: `1.5px solid ${flagged ? COLORS.neonRed + "88" : c + "77"}`,
    }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <span style={{
        flex: 1, fontSize: 21, fontFamily: FONTS.mono,
        color: flagged ? COLORS.textMuted : COLORS.textPrimary,
        textDecoration: flagged ? "line-through" : "none",
      }}>
        {name}
      </span>
      {mode === "cited" ? (
        <span style={{ fontSize: 24, color: COLORS.neonGreen, textShadow: glow(COLORS.neonGreen) }}>✓</span>
      ) : null}
      {mode === "flagged" ? (
        <span style={{ fontSize: 18, color: COLORS.neonRed, fontFamily: FONTS.mono, fontWeight: 700 }}>
          ⚠ unrelated
        </span>
      ) : null}
    </div>
  );
};

/** Small "Sources (N)" header used above a source list. */
export const SourceHeader: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div style={{
    fontSize: 19, fontFamily: FONTS.mono, fontWeight: 700, color,
    letterSpacing: 2, textTransform: "uppercase", marginBottom: 16,
  }}>
    {text}
  </div>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
